import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const HERO_IMAGES = [
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/tcdd0kvs_Bg%201.png",
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/1u75woe1_bg2.png",
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/e871837b0c7a7cdf_Group%20660.png",
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/sw7ev6o5_bg%204.png",
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/cl2u0ybm_bg5.png",
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/9z2f0dlo_Frame%20605.png",
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/m8t5nfxu_Frame%20606.png",
];

// Phone-only slide set — portrait posters shown instead of the desktop slides
const MOBILE_HERO_IMAGES = [
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/hjyf2zu3_99%20songs.jpg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/bdc8e961d6b0b939_MV5BNGFmZDc2YjAtOWFlYi00OTJkLTllOTYtODllZDRjM2EzYWRiXkEyXkFqcGc%40._V1_.jpg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/0wcrq8zv_one%20heart.jpg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_determined-darwin-6/artifacts/pa0zjxb3_MV5BNTZlYzA5ZDctN2YyMC00ZDQ5LWE1MTMtZDdhYWJmNWQ0N2M0XkEyXkFqcGc%40._V1_.jpg",
];

export default function Hero() {
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 639px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const images = isMobile ? MOBILE_HERO_IMAGES : HERO_IMAGES;

  // Restart from the first slide when switching between phone/desktop sets
  useEffect(() => {
    setIndex(0);
  }, [isMobile]);

  // Preload all hero images to prevent flicker / blank frames
  useEffect(() => {
    [...HERO_IMAGES, ...MOBILE_HERO_IMAGES].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, 5000);
    return () => clearInterval(t);
  }, [images]);

  const goTo = (i) => setIndex(i);

  return (
    <section data-testid="hero-section" className="relative h-[100svh] min-h-[600px] w-full overflow-hidden bg-black">
      {/* Dissolve image layer — images crossfade (blend) into one another */}
      <div className="absolute inset-0 bg-black">
        {images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            data-testid={`hero-slide-${i}`}
            aria-hidden={i !== index}
            className="absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-[1600ms] ease-in-out"
            style={{ opacity: i === index ? 1 : 0 }}
          />
        ))}
      </div>

      {/* Cinematic overlays — stronger on the left for text readability (desktop only) */}
      <div className="absolute inset-0 hidden bg-gradient-to-r sm:block sm:from-black/85 sm:via-black/45 sm:to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
      {/* Phone-only top gradient so the navbar stays readable over bright posters */}
      <div className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-black/70 to-transparent sm:hidden" />

      {/* Fixed left content — does not change with slides (anchored to bottom on phones) */}
      <div className="relative z-10 flex h-full flex-col justify-end px-6 pb-24 sm:justify-center sm:pb-0 sm:pl-10 lg:pl-[4vw] lg:pr-6">
        <div className="max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="font-playfair text-[2.6rem] font-bold leading-[1.02] tracking-tight text-white sm:text-6xl sm:leading-[0.98] lg:text-7xl xl:text-8xl"
          >
            <span className="block">Film.</span>
            <span className="block text-gold">Music.</span>
            <span className="block">Live.</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-8 sm:mt-10"
          >
            <Link
              to="/productions"
              data-testid="hero-cta-explore"
              className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-black transition-all duration-300 hover:bg-gold hover:text-white sm:px-9 sm:py-4"
            >
              Explore Our Work
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            data-testid={`hero-dot-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-gold" : "w-4 bg-white/40"}`}
          />
        ))}
      </div>
    </section>
  );
}
